/*
  FluoFree — winkelwagenlade.

  Basis is Dawn's cart-drawer.js; de klassenamen `cart-drawer` en
  `cart-drawer-items` en de sectienaam `cart-drawer` blijven de afspraak met
  cart.js, product-form.js en assets/ff-koop.js.

  Uitbreiding 2026-09-15 (standaard-feedback fase C):
  - Aantal wijzigen en verwijderen via /cart/change.js met optimistic UI: regel,
    subtotaal en knoptotaal veranderen direct, de server bevestigt daarna. Bij
    een fout (422 of netwerk) gaat de regel terug naar de serverstand en staat
    de melding onder de regel. Geen paginarefresh.
  - Eén pad per wijziging: CartDrawerItems overschrijft onChange en
    updateQuantity, dus er komt geen tweede listener naast die van cart.js.
  - Upsell-formulieren gaan via /cart/add.js; zonder JavaScript posten ze
    gewoon naar /cart/add.
  - Her-renderen vervangt alleen .drawer__inner, houdt de scrollpositie vast en
    zet een al geladen TrustBox terug in plaats van hem opnieuw op te bouwen.
  - TrustBox: het script wordt pas geladen als de lade open is én er een
    widget-element staat (alleen als ff_tp_template_id gevuld is). Eén keer, async.
  - Focus trap ook zonder transitie (prefers-reduced-motion geeft geen
    transitionend), Esc werkt ook als de focus nog buiten de lade staat, en de
    focus gaat bij sluiten terug naar het element dat de lade opende.
*/

const FF_CD_SECTIES = ['cart-drawer', 'cart-icon-bubble'];
const FF_TP_SCRIPT = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

function ffCdUrl(route, fallback) {
  const basis = (window.routes && window.routes[route]) || fallback;
  return basis.replace(/\.js$/, '') + '.js';
}

/* Geld opmaken volgens shop.money_format, alleen voor de optimistische stand;
   de server-render daarna is leidend. */
function ffCdGeld(drawer, cents) {
  const houder = drawer.querySelector('[data-ff-cd-geldformaat]');
  const formaat = (houder && houder.getAttribute('data-ff-cd-geldformaat')) || '€{{amount_with_comma_separator}}';
  const opmaak = (getal, decimalen, duizend, komma) => {
    const vast = (getal / 100).toFixed(decimalen);
    const [heel, dec] = vast.split('.');
    const metPunten = heel.replace(/\B(?=(\d{3})+(?!\d))/g, duizend);
    return dec ? metPunten + komma + dec : metPunten;
  };
  return formaat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, soort) => {
    switch (soort) {
      case 'amount_no_decimals': return opmaak(cents, 0, ',', '.');
      case 'amount_with_comma_separator': return opmaak(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return opmaak(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return opmaak(cents, 2, "'", '.');
      default: return opmaak(cents, 2, ',', '.');
    }
  });
}

/* ---------- optimistische weergave ---------- */

function ffCdSamenvatting(drawer) {
  const sub = drawer.querySelector('[data-ff-cd-subtotaal]');
  const tot = drawer.querySelector('[data-ff-cd-totaal]');
  let subtotaal = 0;
  let verschil = 0;
  let bezig = false;
  drawer.querySelectorAll('[data-ff-cd-regel]').forEach((regel) => {
    const input = regel.querySelector('.quantity__input');
    if (!input) return;
    const server = parseInt(input.getAttribute('value'), 10) || 0;
    const nu = regel.classList.contains('ff-cd__item--weg') ? 0 : parseInt(input.value, 10) || 0;
    subtotaal += (parseInt(regel.dataset.ffCdOrigprijs, 10) || 0) * nu;
    verschil += (parseInt(regel.dataset.ffCdPrijs, 10) || 0) * (nu - server);
    if (nu !== server) bezig = true;
  });
  const voet = drawer.querySelector('.drawer__footer');
  if (voet) voet.toggleAttribute('data-ff-cd-bezig', bezig);
  if (!bezig) {
    /* Terug op de serverstand: de door de server gerenderde bedragen herstellen. */
    [sub, tot].forEach((el) => {
      if (el && el.dataset.ffCdServer !== undefined) el.innerHTML = el.dataset.ffCdServer;
    });
    return;
  }
  if (sub) {
    /* fase-bc: subtotaal = te betalen bedrag, dus zelfde som als de knop. */
    if (sub.dataset.ffCdServer === undefined) sub.dataset.ffCdServer = sub.innerHTML;
    sub.textContent = ffCdGeld(drawer, Math.max(0, (parseInt(sub.dataset.ffCdCents, 10) || 0) + verschil));
  }
  if (tot) {
    if (tot.dataset.ffCdServer === undefined) tot.dataset.ffCdServer = tot.innerHTML;
    tot.textContent = ffCdGeld(drawer, Math.max(0, (parseInt(tot.dataset.ffCdCents, 10) || 0) + verschil));
  }
}

function ffCdToon(drawer, line, aantal) {
  const regel = drawer.querySelector(`#CartDrawer-Item-${line}`);
  if (!regel) return;
  const input = regel.querySelector('.quantity__input');
  const totaal = regel.querySelector('[data-ff-cd-regeltotaal]');
  if (totaal && regel.dataset.ffCdServer === undefined) regel.dataset.ffCdServer = totaal.innerHTML;
  const server = parseInt(input.getAttribute('value'), 10);
  regel.setAttribute('aria-busy', aantal !== server ? 'true' : 'false');
  if (aantal === 0) {
    regel.classList.add('ff-cd__item--weg');
  } else {
    regel.classList.remove('ff-cd__item--weg');
    input.value = aantal;
    if (totaal) {
      totaal.innerHTML = aantal === server
        ? regel.dataset.ffCdServer
        : ffCdGeld(drawer, (parseInt(regel.dataset.ffCdPrijs, 10) || 0) * aantal);
    }
  }
  ffCdSamenvatting(drawer);
}

function ffCdHerstel(drawer, line) {
  const regel = drawer.querySelector(`#CartDrawer-Item-${line}`);
  if (!regel) return;
  const input = regel.querySelector('.quantity__input');
  ffCdToon(drawer, line, parseInt(input.getAttribute('value'), 10));
  regel.removeAttribute('aria-busy');
}

function ffCdMelding(line, tekst) {
  const houder = document.getElementById(`CartDrawer-LineItemError-${line}`);
  if (houder) {
    const t = houder.querySelector('.cart-item__error-text');
    if (t) t.textContent = tekst || '';
  }
  const status = document.getElementById('CartDrawer-LiveRegionText');
  if (status && tekst) status.textContent = tekst;
}

function ffCdFouttekst(data) {
  if (!data) return window.cartStrings && window.cartStrings.error;
  if (typeof data.description === 'string' && data.description) return data.description;
  if (typeof data.errors === 'string' && data.errors) return data.errors;
  if (typeof data.message === 'string' && data.message && data.message !== 'Cart Error') return data.message;
  return window.cartStrings && window.cartStrings.error;
}

/* ---------- renderen ---------- */

function ffCdRender(drawer, sections) {
  const html = sections && sections['cart-drawer'];
  if (html && drawer) {
    const nieuw = new DOMParser().parseFromString(html, 'text/html').querySelector('.drawer__inner');
    const inner = drawer.querySelector('.drawer__inner');
    if (nieuw && inner) {
      const scroll = inner.querySelector('[data-ff-cd-scroll]');
      const top = scroll ? scroll.scrollTop : 0;
      const tp = inner.querySelector('[data-ff-tp-widget][data-ff-tp-klaar]');
      inner.innerHTML = nieuw.innerHTML;
      if (tp) {
        const plek = inner.querySelector('[data-ff-tp-widget]');
        if (plek && plek.getAttribute('data-template-id') === tp.getAttribute('data-template-id')) plek.replaceWith(tp);
      }
      const scroll2 = inner.querySelector('[data-ff-cd-scroll]');
      if (scroll2) scroll2.scrollTop = top;
      drawer.classList.toggle('is-empty', !inner.querySelector('.cart-item'));
    }
  }
  const bubbel = sections && sections['cart-icon-bubble'];
  const doel = document.getElementById('cart-icon-bubble');
  if (bubbel && doel) {
    const bron = new DOMParser().parseFromString(bubbel, 'text/html').querySelector('.shopify-section');
    if (bron) doel.innerHTML = bron.innerHTML;
  }
  if (drawer && drawer.classList.contains('active')) ffCdTrustpilot(drawer);
}

/* De sleutel (line item key) van een regel. Die is positie-onafhankelijk: een
   regel houdt hem ook als er ergens anders in de winkelwagen iets verdwijnt. */
function ffCdSleutel(drawer, line) {
  const regel = drawer && drawer.querySelector(`#CartDrawer-Item-${line}`);
  return (regel && regel.dataset.ffCdKey) || null;
}

/* Lade opnieuw opbouwen vanaf de server. Na een afgewezen wijziging klopt de
   nummering in de DOM niet meer met de winkelwagen; alleen de melding tonen
   laat die scheefstand staan en de volgende klik faalt dan opnieuw. */
function ffCdHerlaad(drawer) {
  const basis = (window.routes && window.routes.cart_url) || '/cart';
  return fetch(`${basis}?section_id=cart-drawer`)
    .then((res) => res.text())
    .then((html) => ffCdRender(drawer, { 'cart-drawer': html }))
    .catch(() => {});
}

function ffCdVolgendeFocus(drawer, line, naam) {
  const container = document.getElementById('CartDrawer');
  if (!container || !drawer.classList.contains('active') || typeof trapFocus !== 'function') return;
  const regel = document.getElementById(`CartDrawer-Item-${line}`);
  let doel = naam && regel ? regel.querySelector(`[name="${naam}"]`) : null;
  if (!doel) doel = drawer.querySelector('.cart-item__name');
  if (!doel && drawer.classList.contains('is-empty')) {
    const leeg = drawer.querySelector('.drawer__inner-empty');
    if (leeg) return trapFocus(leeg, leeg.querySelector('a'));
  }
  trapFocus(container, doel || drawer.querySelector('.drawer__close'));
}

/* ---------- TrustBox ---------- */

function ffCdTrustpilot(drawer) {
  if (!drawer || !drawer.classList.contains('active')) return;
  const widgets = [...drawer.querySelectorAll('[data-ff-tp-widget]:not([data-ff-tp-klaar])')];
  if (!widgets.length) return;
  const bouw = () => {
    widgets.forEach((w) => {
      if (!w.isConnected || w.hasAttribute('data-ff-tp-klaar')) return;
      w.setAttribute('data-ff-tp-klaar', '');
      if (w.querySelector('iframe')) return; /* het bootstrap-script heeft hem zelf al opgebouwd */
      try { window.Trustpilot.loadFromElement(w, true); } catch (e) { /* terugval: de link in de widget blijft staan */ }
    });
  };
  if (window.Trustpilot && typeof window.Trustpilot.loadFromElement === 'function') return bouw();
  let script = document.querySelector('script[data-ff-tp-bootstrap], script[src*="tp.widget.bootstrap"]');
  if (!script) {
    script = document.createElement('script');
    script.async = true;
    script.src = FF_TP_SCRIPT;
    script.setAttribute('data-ff-tp-bootstrap', '');
    document.head.appendChild(script);
  }
  if (!script.hasAttribute('data-ff-cd-wacht')) {
    script.setAttribute('data-ff-cd-wacht', '');
    script.addEventListener('load', () => ffCdTrustpilot(document.querySelector('cart-drawer')), { once: true });
  }
}

/* ---------- upsell ---------- */

function ffCdUpsell(drawer, form) {
  const knop = form.querySelector('button[type="submit"]');
  const item = form.closest('[data-ff-cd-upsell]');
  const fout = item && item.querySelector('[data-ff-cd-uperr]');
  const id = parseInt((form.querySelector('[name="id"]') || {}).value, 10);
  if (!knop || knop.disabled || !id) return;
  const label = knop.textContent;
  knop.disabled = true;
  knop.setAttribute('aria-busy', 'true');
  if (knop.dataset.ffCdBezigLabel) knop.textContent = knop.dataset.ffCdBezigLabel;
  if (fout) fout.textContent = '';

  fetch(ffCdUrl('cart_add_url', '/cart/add'), {
    ...fetchConfig('json'),
    body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: FF_CD_SECTIES, sections_url: window.location.pathname }),
  })
    .then(async (res) => {
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.status) {
        knop.disabled = false;
        knop.removeAttribute('aria-busy');
        knop.textContent = label;
        if (fout) fout.textContent = ffCdFouttekst(data);
        return;
      }
      if (item) item.hidden = true;
      ffCdRender(drawer, data.sections);
      ffCdVolgendeFocus(drawer, null, null);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ff-cart-drawer', cartData: data, variantId: id });
    })
    .catch(() => {
      knop.disabled = false;
      knop.removeAttribute('aria-busy');
      knop.textContent = label;
      if (fout) fout.textContent = window.cartStrings && window.cartStrings.error;
    });
}

/* ---------- lade ---------- */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    /* Esc ook als de focus nog niet in de lade staat (bv. direct na openen). */
    document.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape' && this.classList.contains('active')) this.close();
    });
    /* Overlay via delegatie: blijft werken nadat de inhoud opnieuw is gerenderd. */
    this.addEventListener('click', (evt) => {
      if (evt.target && evt.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.addEventListener('submit', (evt) => {
      const form = evt.target.closest && evt.target.closest('[data-ff-cd-upform]');
      if (!form) return;
      evt.preventDefault();
      ffCdUpsell(this, form);
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const opener = triggeredBy || (document.activeElement !== document.body ? document.activeElement : null);
    if (opener && !this.contains(opener)) this.setActiveElement(opener);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      ffCdTrustpilot(this);
    });

    const beurt = (this.ffOpenBeurt = (this.ffOpenBeurt || 0) + 1);
    const vang = () => {
      if (this.ffGevangen === beurt || !this.classList.contains('active')) return;
      this.ffGevangen = beurt;
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };
    this.addEventListener('transitionend', vang, { once: true });
    /* Zonder transitie (prefers-reduced-motion) komt er geen transitionend. */
    setTimeout(vang, 360);

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    this.ffGevangen = null;
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /* Aangeroepen door ff-koop.js en product-form.js na /cart/add.js. */
  renderContents(parsedState) {
    this.productId = parsedState.id;
    ffCdRender(this, parsedState.sections || {});
    setTimeout(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

let ffCdWachtrij = Promise.resolve();

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    /* Direct tonen, zonder debounce en zonder verzoek; het verzoek volgt via
       de gedebouncede onChange uit cart.js. */
    this.addEventListener('change', (event) => {
      const input = event.target;
      if (!input.classList || !input.classList.contains('quantity__input')) return;
      const aantal = parseInt(input.value, 10);
      if (Number.isNaN(aantal) || aantal < 0) return;
      ffCdMelding(input.dataset.index, '');
      ffCdToon(this.closest('cart-drawer'), input.dataset.index, aantal);
    });
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items' || event.source === 'ff-cart-drawer') return;
      return this.onCartUpdate();
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /* Wijziging van buiten de lade (bv. product-form.js): de hele lade verversen. */
  onCartUpdate() {
    const drawer = this.closest('cart-drawer');
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((html) => ffCdRender(drawer, { 'cart-drawer': html }))
      .catch((e) => console.error(e));
  }

  onChange(event) {
    const input = event.target;
    if (!input.classList || !input.classList.contains('quantity__input')) return;
    const line = parseInt(input.dataset.index, 10);
    const server = parseInt(input.getAttribute('value'), 10);
    const aantal = parseInt(input.value, 10);
    if (Number.isNaN(aantal) || aantal < 0) {
      ffCdHerstel(this.closest('cart-drawer'), line);
      return;
    }
    if (aantal === server) {
      ffCdHerstel(this.closest('cart-drawer'), line);
      return;
    }
    const naam = document.activeElement && document.activeElement.getAttribute('name');
    this.updateQuantity(line, aantal, event, naam, input.dataset.quantityVariantId, input.dataset.key);
  }

  /* `sleutel` is optioneel: de verwijderknop uit cart.js roept deze methode met
     vier argumenten aan. Staat hij er niet bij, dan komt hij uit de DOM. */
  updateQuantity(line, quantity, event, name, variantId, sleutel) {
    const drawer = this.closest('cart-drawer');
    line = parseInt(line, 10);
    quantity = parseInt(quantity, 10);
    const key = sleutel || ffCdSleutel(drawer, line);
    ffCdMelding(line, '');
    ffCdToon(drawer, line, quantity);
    const volgnummer = (drawer.ffVolgnummer = (drawer.ffVolgnummer || 0) + 1);
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = '';

    /* Adresseren op de line item key, niet op de positie. `line` telt vanaf de
       DOM op klikmoment; zodra die nummering één stap achterloopt op de echte
       winkelwagen — twee snelle klikken achter elkaar, of een app die buiten het
       thema om een regel weghaalt — wijst hij voorbij het einde en antwoordt
       Shopify met 422 "Parameter line is ongeldig." De sleutel heeft dat
       probleem niet. Zonder sleutel (oude gerenderde lade) valt hij terug op
       `line`. */
    const body = JSON.stringify({
      ...(key ? { id: key } : { line }),
      quantity,
      sections: FF_CD_SECTIES,
      sections_url: window.location.pathname,
    });

    /* Verzoeken na elkaar: twee snelle klikken mogen elkaar niet inhalen. */
    ffCdWachtrij = ffCdWachtrij
      .then(() => fetch(ffCdUrl('cart_change_url', '/cart/change'), { ...fetchConfig('json'), body }))
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok || !data || data.errors || (data.status && data.status >= 400)) {
          ffCdHerstel(drawer, line);
          ffCdMelding(line, ffCdFouttekst(data));
          /* De winkelwagen is anders dan de lade denkt: opnieuw ophalen, zodat
             de volgende klik weer op de goede regel landt. */
          return ffCdHerlaad(drawer);
        }
        if (drawer.ffVolgnummer !== volgnummer) return; /* een nieuwere wijziging rendert straks */
        const regelsVoor = drawer.querySelectorAll('[data-ff-cd-regel]').length;
        ffCdRender(drawer, data.sections);
        const nieuw = data.items && (key ? data.items.find((i) => i.key === key) : data.items[line - 1]);
        if (quantity > 0 && regelsVoor === data.items.length && nieuw && nieuw.quantity !== quantity) {
          ffCdMelding(line, window.cartStrings.quantityError.replace('[quantity]', nieuw.quantity));
        }
        ffCdVolgendeFocus(drawer, line, name);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: data, variantId: variantId });
      })
      .catch(() => {
        ffCdHerstel(drawer, line);
        if (errors) errors.textContent = window.cartStrings && window.cartStrings.error;
      });
    return ffCdWachtrij;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
